import { createRouter, createWebHistory, type RouteLocationNormalized, type NavigationGuardNext } from 'vue-router';
import { useAuth } from '../composables/useAuth';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'welcome',
      component: (): Promise<typeof import('*.vue')> => import('../views/WelcomeView.vue'),
      meta: { autoRedirect: true },
    },
    {
      path: '/callback/auth',
      name: 'auth-callback',
      component: (): Promise<typeof import('*.vue')> => import('../views/WelcomeView.vue'),
      meta: { autoRedirect: false },
    },
    {
      path: '/home',
      name: 'home',
      component: (): Promise<typeof import('*.vue')> => import('../views/WelcomeView.vue'),
      meta: { autoRedirect: false },
    },
    {
      path: '/player',
      name: 'player',
      component: (): Promise<typeof import('*.vue')> => import('../views/PlayerView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/guide',
      name: 'user-guide',
      component: (): Promise<typeof import('*.vue')> => import('../views/UserGuideView.vue'),
    },
    {
      path: '/pricing',
      name: 'pricing',
      component: (): Promise<typeof import('*.vue')> => import('../views/PricingView.vue'),
    },
    {
      path: '/container-setup',
      name: 'container-setup',
      component: (): Promise<typeof import('*.vue')> => import('../views/ContainerSetupView.vue'),
    },
    {
      path: '/support',
      name: 'support',
      component: (): Promise<typeof import('*.vue')> => import('../views/SupportView.vue'),
    },
    {
      path: '/acknowledgements',
      name: 'acknowledgements',
      component: (): Promise<typeof import('*.vue')> => import('../views/AcknowledgementsView.vue'),
    },
    {
      path: '/user-status',
      name: 'user-status',
      component: (): Promise<typeof import('*.vue')> => import('../views/UserStatusView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/admin',
      name: 'admin',
      component: (): Promise<typeof import('*.vue')> => import('../views/AdminView.vue'),
      meta: { requiresAdmin: true },
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    // If there's a saved position (browser back/forward), use it
    if (savedPosition) {
      return savedPosition;
    }
    // If there's a hash (anchor), scroll to it after DOM renders
    if (to.hash) {
      return new Promise((resolve) => {
        // Use requestAnimationFrame to wait for next paint, ensuring DOM is ready
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const element = document.querySelector(to.hash);
            if (element) {
              resolve({
                el: to.hash,
                behavior: 'smooth',
                top: 80, // Offset for header height
              });
            } else {
              // Element not found, scroll to top
              resolve({ top: 0 });
            }
          });
        });
      });
    }
    // Otherwise, scroll to top
    return { top: 0 };
  },
});

// Navigation guard for protected routes
router.beforeEach(async (to, from, next) => {
  const auth = useAuth();

  // Wait for auth to finish loading
  if (auth.isLoading.value) {
    // Wait for auth to be ready (max 5 seconds)
    let attempts = 0;
    while (auth.isLoading.value && attempts < 50) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
  }

  checkAuth(to, from, next, auth);
});

function checkAuth(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext, auth: ReturnType<typeof useAuth>): void {
  // Check admin routes
  if (to.meta.requiresAdmin) {
    if (auth.isAdmin.value) {
      next();
    } else {
      alert('Access denied. Admin privileges required.');
      next('/');
    }
    return;
  }

  // Check auth routes (player)
  if (to.meta.requiresAuth) {
    // Local mode: No auth required
    if (auth.isLocalMode.value) {
      next();
      return;
    }

    // Cloud mode: Require authentication
    if (auth.isAuthenticated.value) {
      next();
    } else {
      // Redirect to welcome page and trigger login
      alert('Please log in to access the player.');
      next('/');
    }
    return;
  }

  // All other routes are public
  next();
}

export default router;
